import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSubEventsByOrg,
  selectSubEvents,
  selectSubEventLoading,
} from "@/store/redux/slices/event/sub_event_slice";
import Back from "@/components/specific/Back";
import SubEventHeader from "@/pages/event_management/single_sub_event/components/Sub_Events_Header";
import LocationMap from "@/pages/event_management/single_sub_event/components/Location_Map";
import ScheduleSection from "@/pages/event_management/single_sub_event/components/sub_event_schedule_tabs/Schedule_Section";
import AttendeesSection from "@/pages/event_management/single_sub_event/components/sub_event_attendees_tab/Attendees_Section";
import AssignedMembers from "@/pages/event_management/single_sub_event/components/Assigned_Members";
import AllocatedFundsCard from "@/pages/event_management/single_sub_event/components/Allocated_Funds_Card";
import Breadcrumb from "@/components/specific/Breadcrumb";
import Success from "@/components/common/alerts/Success";
import Error from "@/components/common/alerts/Error";
import { useLoader } from "@/hooks/LoaderProvider";

const SingleSubEvent = () => {
  const { eventId, id } = useParams();
  const location = useLocation();
  const parentEventName = location.state?.parentEventName || "Event";
  const { showLoader, hideLoader } = useLoader();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const subEvents = useSelector(selectSubEvents);
  const isLoading = useSelector(selectSubEventLoading);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [showModifySuccess, setShowModifySuccess] = useState(false);
  const [showScheduleSuccess, setShowScheduleSuccess] = useState(false);
  const [scheduleSuccessTitle, setScheduleSuccessTitle] = useState("");
  const [scheduleSuccessMessage, setScheduleSuccessMessage] = useState("");
  const [showScheduleError, setShowScheduleError] = useState(false);
  const [scheduleErrorTitle, setScheduleErrorTitle] = useState("");
  const [scheduleErrorMessage, setScheduleErrorMessage] = useState("");

  useEffect(() => {
    // Show global loader when loading
    if (isLoading) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isLoading, showLoader, hideLoader]);

  useEffect(() => {
    if (id) {
      const fetchSubEvents = async () => {
        const res = await dispatch(
          fetchSubEventsByOrg({
            key: "_id",
            value: id,
          })
        ).unwrap();
        console.log("sub event data:", res);
      };
      fetchSubEvents();
    }
  }, [dispatch, id]);

  const handleBackClick = () => {
    navigate(`/dashboard/events/${eventId}`);
  };

  // Get the first item since it's a single sub-event
  const subEvent = subEvents?.[0];

  // Only render the content when we have data
  if (!isLoading && !subEvent) {
    return <div>Sub-event not found</div>;
  }

  const handleDeleteSuccess = () => {
    setShowDeleteSuccess(true);
  };

  const handleDeleteSuccessClose = () => {
    setShowDeleteSuccess(false);
    navigate(`/dashboard/events/${eventId}`);
  };

  const handleModifySuccess = () => {
    setShowModifySuccess(true);
  };

  const handleModifySuccessClose = () => {
    setShowModifySuccess(false);
  };

  const handleScheduleSuccess = (mode) => {
    setScheduleSuccessTitle(
      mode === "add" ? "Schedule Added!" : "Schedule Updated!"
    );
    setScheduleSuccessMessage(
      mode === "add"
        ? "The schedule has been successfully added to your event."
        : "The schedule has been successfully updated."
    );
    setShowScheduleSuccess(true);
  };

  const handleScheduleSuccessClose = () => {
    setShowScheduleSuccess(false);
  };

  const handleScheduleError = (mode, errorMsg) => {
    setScheduleErrorTitle(
      mode === "add" ? "Failed to Add Schedule" : "Failed to Update Schedule"
    );
    setScheduleErrorMessage(
      errorMsg || "There was an error processing your request."
    );
    setShowScheduleError(true);
  };

  const handleScheduleErrorClose = () => {
    setShowScheduleError(false);
  };

  // Updated breadcrumb with passed event name and sub-event name
  const breadcrumbItems = [
    {
      title: parentEventName,
      link: `/dashboard/events/${eventId}`,
    },
    {
      title: subEvent?.name || "Schedule",
      link: "",
    },
  ];

  return (
    <div className="w-full">
      <Breadcrumb items={breadcrumbItems} />
      <Back text="Back" onClick={handleBackClick} />

      {!isLoading && (
        <>
          <SubEventHeader
            subEvent={subEvent}
            onDeleteSuccess={handleDeleteSuccess}
            onModifySuccess={handleModifySuccess}
          />

          {/* Success Modal */}
          <Success
            isVisible={showDeleteSuccess}
            onClose={handleDeleteSuccessClose}
            header="Sub Event Deleted"
            paragraph="Sub event has been deleted successfully!"
            buttonText="Done"
            showCancelButton={false}
            onButtonClick={handleDeleteSuccessClose}
          />

          {/* Modify Success Modal */}
          <Success
            isVisible={showModifySuccess}
            onClose={handleModifySuccessClose}
            header="Sub Event Modified"
            paragraph="Sub event has been modified successfully!"
            buttonText="Done"
            showCancelButton={false}
            onButtonClick={handleModifySuccessClose}
          />

          {/* Schedule Success Modal */}
          <Success
            isVisible={showScheduleSuccess}
            onClose={handleScheduleSuccessClose}
            header={scheduleSuccessTitle}
            paragraph={scheduleSuccessMessage}
            buttonText="Done"
            showCancelButton={false}
            onButtonClick={handleScheduleSuccessClose}
          />

          {/* Schedule Error Modal */}
          <Error
            isVisible={showScheduleError}
            onClose={handleScheduleErrorClose}
            header={scheduleErrorTitle}
            paragraph={scheduleErrorMessage}
            buttonText="Close"
            onButtonClick={handleScheduleErrorClose}
          />

          <LocationMap location={subEvent?.location} />

          <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:gap-8 lg:grid-cols-3 w-full max-w-full overflow-hidden">
            <div className="col-span-full lg:col-span-2">
              <div className="mt-4 lg:mt-8">
                <p className="text-left text-small text-darGrey">
                  {subEvent.description}
                </p>
              </div>
              <ScheduleSection
                schedule={subEvent.schedule}
                duration={subEvent.duration}
                onScheduleSuccess={handleScheduleSuccess}
                onScheduleError={handleScheduleError}
              />
              <AttendeesSection members={subEvent.members} />
            </div>
            <div className="col-span-full lg:col-span-1">
              <AssignedMembers speakers={subEvent.schedule.speakers} />
              <AllocatedFundsCard subEvent={subEvent} spentPercentage={0} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SingleSubEvent;
